import React from 'react';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';
import ChatTestimonials from './ChatTestimonials';

const BeforeAfterNew = ({ onRegisterClick, settings }) => {
  return (
    <div className="py-24 bg-[#0A0A0B] text-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Signature Control Section */}
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-12 leading-tight">
              Your Signature Is Quietly Controlling Your <span className="italic text-[#F5A623]">Wealth Results</span><br />
              — Whether You Know It or Not
            </h2>
            
            <div className="max-w-3xl mx-auto mb-16">
              <div className="bg-white p-8 rounded-2xl shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_feng-shui-signup/artifacts/0ibbtbii_tmpuyzxxs72.webp"
                  alt="Signature Mistakes Checklist"
                  className="w-full h-auto rounded-xl"
                />
              </div>
            </div>

            {/* Signature Correction Benefits */}
            <div className="max-w-4xl mx-auto mb-16 text-center">
              <p className="text-3xl font-bold text-white mb-8 leading-relaxed">
                Your signature is one of the most repeated structures in your life.
              </p>
              <p className="text-2xl font-semibold text-[#F5A623] mb-4">When corrected properly:</p>
              <div className="space-y-3 mb-12">
                <p className="text-2xl text-white">Energy stops leaking</p>
                <p className="text-2xl text-white">Effort becomes effective</p>
                <p className="text-2xl text-white">Identity becomes coherent</p>
              </div>

              {/* Stop/Start Image */}
              <div className="mb-12">
                <img 
                  src="https://customer-assets.emergentagent.com/job_feng-shui-signup/artifacts/73qzbf04_tmp28549w3c.webp"
                  alt="Stop Guessing, Stop Copying, Start Aligning"
                  className="w-full max-w-2xl mx-auto h-auto rounded-3xl"
                />
              </div>

              <h3 className="text-4xl font-black text-white mb-8">
                READY TO DECODE YOUR <span className="text-[#F5A623]">WEALTH SIGNATURE</span>?
              </h3>

              <Button 
                onClick={onRegisterClick}
                className="bg-[#F5A623] hover:bg-[#E59400] text-black font-bold px-12 py-6 text-xl rounded-lg group shadow-2xl"
              >
                Join the Signature of Wealth Webinar
                <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className="text-gray-400 mt-4">{settings?.date || '19th March 2026 (Thursday)'} at {settings?.time || '8.00PM'}</p>
            </div>
          </div>

          <ChatTestimonials />

          <div className="text-center mb-16">
            <div className="max-w-4xl mx-auto mb-12">
              <p className="text-xl text-gray-300 leading-relaxed mb-4">
                A signature without a "money bag" stroke—represented by well-formed loops in letters like 'g,' 'j,' and 'y', 's' —often symbolizes a lack of financial stability, weak money management skills, and limited abundance. In feng shui and graphology, these loops indicate an individual's relationship with wealth. When absent, it may suggest difficulties in accumulating savings, challenges in financial planning, or even an unconscious tendency to let money slip away.
              </p>
              <p className="text-2xl font-bold text-[#F5A623] leading-relaxed">
                You need to find out WHY via our webinar. Join the webinar today!
              </p>
            </div>
            
            <h2 className="text-5xl font-bold mb-8">
              Do you have any of these <span className="italic text-[#F5A623]">mistakes</span>?
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed max-w-4xl mx-auto">
              Another common mistake that 90% of people make in their signatures is adding underlines and dots. While these marks may seem like small details, they actually symbolize rigidity, stubbornness, and being locked into certain principles. These habits can create energetic blockages, limiting new opportunities and flexibility in life.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-16">
            {/* BEFORE Analysis */}
            <div>
              <div className="text-center mb-6">
                <span className="inline-block bg-red-500/20 text-red-400 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider">
                  Before Analysis
                </span>
              </div>
              <div className="bg-[#1A1A1D] border border-red-500/30 rounded-2xl p-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_feng-shui-signup/artifacts/7vs76wig_tmpuzwgtkxl.webp"
                  alt="Before Signature Analysis - Chaotic Signature"
                  className="w-full h-auto object-contain rounded-xl"
                />
                <div className="mt-6">
                  <p className="text-red-400 font-semibold text-sm text-center">
                    ❌ Common chaotic strokes with underline and poor structure
                  </p>
                </div>
              </div>
            </div>

            {/* AFTER Analysis */}
            <div>
              <div className="text-center mb-6">
                <span className="inline-block bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider">
                  After Analysis
                </span>
              </div>
              <div className="bg-[#1A1A1D] border border-green-500/30 rounded-2xl p-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_feng-shui-signup/artifacts/xospzc9m_tmpxlvsw_v5.webp"
                  alt="After Signature Analysis - Corrected with Markings"
                  className="w-full h-auto object-contain rounded-xl"
                />
                <div className="mt-6">
                  <p className="text-red-400 font-semibold text-sm text-center">
                    ❌ Chaotic strokes with swords represents weak energy and stumble block
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Button 
              onClick={onRegisterClick}
              className="bg-[#F5A623] hover:bg-[#E59400] text-black font-bold px-8 py-6 text-lg rounded-lg group"
            >
              Join the Signature of Wealth Webinar
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <p className="text-gray-400 mt-4">{settings?.date || '19th March 2026 (Thursday)'} at {settings?.time || '8.00PM'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterNew;